import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Assumptions } from '../static/Assumptions';
import { DiagnosisShort } from './DiagnosisShort';
import { RX } from './RX';
import { Diagnosis } from './Diagnosis';
import { Surgery } from './Surgery';
import { HIVStateOptions } from './HIVStateOptions';
import { Customer } from './Customer';
import { Utils } from '../Utils';
import { Address } from './Address';
import { PersonalHistory as GQLPersonalHistory } from '@hit/hit-api-client';

@supertypeClass({ toClient: true, toServer: true })
export class PersonalHistory extends Everything implements GQLPersonalHistory {
  primaryPhysicianLastVisitWhenTrigger() {
    if (this.primaryPhysicianLastVisitWhen === 'unknown') {
      this.primaryPhysicianLastVisit = null;
    }
  }

  @property({ comment: 'Autism Spectrum Disorder' })
  hadBDAutism: boolean = false;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDAutismDetails: DiagnosisShort;

  @property({ comment: 'Cerebral Palsy' })
  hadDBCerebralPalsy: boolean = false;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you currently have high blood pressure',
  })
  hadHCHypertensionCurrent: boolean = false;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBCerebralPalsyDetails: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDLymeDiseaseDetails: DiagnosisShort;

  @property({ comment: 'Down Syndrome' })
  hadDBDownSyndrome: boolean = false;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: {
      Yes: 'Yes',
      No: 'No',
      NotSure: 'Not Sure',
    },
    comment: 'Do you have multiple Traumatic brain injuries',
  })
  hasTBIMultiple: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBDownSyndromeDetails: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: {
      Yes: 'Yes',
      No: 'No',
      NotSure: 'Not Sure',
    },
  })
  hadEOHospitalizedLast10Years: string;

  @property({
    rule: ['required'],
    values: ['lessthan1', '1to5', 'morethan5'],
    descriptions: {
      'lessthan1': 'Less than 1',
      '1to5': '1 to 5',
      'morethan5': 'More than 5',
    },
  })
  hadEODiagnosisPeriod: string;

  @property({
    rule: ['required'],
    values: ['1to2', '3to4', '5ormore'],
    descriptions: {
      '1to2': '1 to 2',
      '3to4': '3 to 4',
      '5ormore': '5 or more',
    },
    comment: 'How many times per week do you use CPAP',
  })
  hasRDCPAPWeeklyFrequency: string;

  @property({
    rule: ['required'],
    values: ['0to4', '4to6', 'morethan6'],
    descriptions: {
      '0to4': '0 to 4',
      '4to6': '4 to 6',
      'morethan6': 'more than 6',
    },
    comment: 'How many hours per night do you use CPAP',
  })
  hasRDCPAPNightlyHours: string;

  @property({ isLocal: true, persist: false })
  residencyStatusTrigger;

  @property({ comment: 'No medical treatments needed' })
  noTreatmentsNeeded: boolean = false;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you have a primary physician',
  })
  primaryPhysician: boolean;

  @property({ rule: ['required'], validate: 'isNotEmptyAndMinLength(2)', comment: "Primary physician's name" })
  primaryPhysicianName: string;

  @property({
    rule: ['required'],
    validate: 'isNotEmptyAndMinLength(2); isMaxLength(86)',
    comment: "Primary physician's City",
  })
  primaryPhysicianCity: string;

  @property({ rule: ['required'], values: Assumptions.stateValues, comment: "Primary physician's State" })
  primaryPhysicianState: string;

  @property({ rule: ['required'], comment: 'What HIV procedure or test was recommended' })
  procedure4HIVTest: string;

  @property({ rule: ['required', 'telephone'], comment: "Primary physician's phone number" })
  primaryPhysicianPhone: string;

  @property({
    rule: ['required'],
    values: ['lastvisit', 'unknown'],
    descriptions: { lastvisit: '__ Date', unknown: 'Unknown' },
    comment: 'When did you last see your primary physician (approximately)',
  })
  primaryPhysicianLastVisitWhen: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'In the past 10 years, have you been diagnosed/treated with abnomal heart condition',
  })
  hadHeartCondition: boolean;

  @property({ comment: 'Had heart attack' })
  hadHCAttack: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCAttackDiagnosis: DiagnosisShort;

  @property({ comment: 'Had abnormal heartbeat' })
  hadHCAbnormalBeat: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you have a history of atrial fibrillation or atrial flutter',
  })
  hadHCABFibrillation: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Has your abnormal heart beat been treated with medication, surgery or electrical shock',
  })
  hadHCABFTreatment: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Did your abnormal heart beat ever cause you to faint or lose consciousness',
  })
  hadHCABFTFainted: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCAbnormalBeatDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'High blood pressure / hypertension',
  })
  hadHCHypertension: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you taking medication to control your hypertension or high blood pressure',
  })
  hadHCHMedication: boolean;

  @property({ type: Array, getType: () => RX })
  medsHCH: Array<RX> = [];

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCHMedicationDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Current blood pressure measurements answered by customer',
  })
  bpMeasurements: boolean;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(80, 200)', comment: 'Top number of blood pressure' })
  systolic: number;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(50, 200)',
    comment: 'Bottom number of blood pressure',
  })
  diastolic: number;

  @property({ comment: 'Heart Murmur or Heart Valve Problem (including Mitral Valve Prolapse)' })
  hadHCMurmur: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you been advised to have your heart periodically tested with imaging (echocardiogram)',
  })
  hadHCMAdviseECG: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you been advised to have surgery for your heart murmur or heart valve problem',
  })
  hadHCMAdviseSurgery: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you been advised to have surgery for your heart murmur or heart valve problem',
  })
  hadHCMAdviseMeds: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCMurmurDiagnosis: DiagnosisShort;

  @property({ comment: 'Pericarditis(inflammation of the pericardium)' })
  hadHCPericarditis: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you had more than one episode',
  })
  hadHCPMultipleEpisodes: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Have you fully recovered',
  })
  hadHCPFullyRecovered: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCPericarditisDiagnosis: DiagnosisShort;

  @property({ comment: 'Varicose Veins Venous Insufficiency' })
  hadHCBenign: boolean;

  @property({ comment: 'Heart disorder - Other/Not Sure' })
  hadHCOther: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  hadHCOtherDiagnosis: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Elevated cholesterol or triglyceride levels (hyperlipidemia)',
  })
  hadHighCholesterol: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you currently take medication for your cholesterol',
  })
  hasMedicationCholesterol: boolean;

  @property({ type: Array, getType: () => RX, comment: 'List of medications taken for cholesterol' })
  medsHCCholesterol: Array<RX> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you currently have elevated cholesterol levels',
  })
  hadHCElevatedNow: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you know your cholesterol levels',
  })
  hadHCKnowLevels: boolean;

  @property({ comment: 'Had an aortic aneurysm' })
  hadHCAorticAneurysm: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCAorticAneurysmDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Chest Pains' })
  hadHCChestPains: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCChestPainsDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Testicular Cancer' })
  hadCncrTesticular: boolean;

  @property({ rule: ['required'], comment: 'Testicular Cancer details', getType: () => DiagnosisShort })
  hadCncrTesticularDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Peripheral Artery Vein Disease' })
  hadHCPeripheralArteryVeinDisease: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadHCPeripheralArteryVeinDiseaseDiagnosis: DiagnosisShort;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 500)', comment: 'What is your total cholesterol' })
  cholesterolTotal: number;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 200)', comment: 'What is your HDL' })
  cholesterolHDL: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Any malignant tumor or cancer including skin cancer, leukemia and/or lymphoma',
  })
  hadCancer: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCancerDiagnosis: DiagnosisShort;

  @property()
  hadLeukemia: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadLeukemiaDiagnosis: DiagnosisShort;

  @property()
  hadLymphoma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadLymphomaDiagnosis: DiagnosisShort;

  @property()
  hadSkinCncrMelanoma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadSkinCncrMelanomaDiagnosis: DiagnosisShort;

  @property({ comment: 'Skin Cancer - Other' })
  hadSkinCncrOther: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadSkinCncrOtherDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Breast Cancer' })
  hadCncrBreast: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCncrBreastDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Brain Cancer' })
  hadCncrBrain: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCncrBrainDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Colon Cancer' })
  hadCncrColon: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCncrColonDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Ovarian Cancer' })
  hadCncrOvarian: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCncrOvarianDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Prostate Cancer' })
  hadCncrProstate: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCncrProstateDiagnosis: DiagnosisShort;

  @property({ comment: 'Had Renal Cancer' })
  hadCncrRenal: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCncrRenalDiagnosis: DiagnosisShort;

  @property({ comment: 'Cholesterol - Other/Not Sure' })
  hadCholestOther: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  hadCholestOtherDiagnosis: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A disorder of the blood, spleen or immune system including anemia, blood clots, bleeding, or immune deficiency',
  })
  hadBloodDisorder: boolean;

  @property({
    rule: ['required'],
    comment: 'Iron or Vitamin Deficiency (without Anemia), Thalassemia Trait or Sickle Cell Trait (without Anemia)',
  })
  hadNonAnemiaDeficiencyOrBloodDisorder: boolean;

  @property({ comment: 'Have you had anemia' })
  hadBDAnemia: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDAnemiaDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['IronDeficient', 'Pernicious', 'Hemolytic', 'Other/NotSure'],
    descriptions: {
      'IronDeficient': 'Iron Deficient',
      'Pernicious': 'Pernicious',
      'Hemolytic': 'Hemolytic',
      'Other/NotSure': 'Other / Not Sure',
    },
    comment: 'Which type of anemia',
  })
  hadBDAnemiaType: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your anemia currently resolved or controlled with oral iron pills/supplements',
  })
  hasBDAnemiaResolved: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBDAnemiaResolvedDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your iron deficiency due to chronic bleeding other than menstrual bleeding',
  })
  hadBDAnemiaBleeding: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDAnemiaBleedingDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your anemia adequately controlled with vitamin B12 supplements (oral or injected)',
  })
  hasBDAnemiaControlled: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBDAnemiaControlledDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you currently have symptoms related to your anemia',
  })
  hasBDAnemiaSymptoms: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBDAnemiaSymptomsDiagnosis: DiagnosisShort;

  @property({
    comment:
      'Overactive spleen (hypersplenism), low platelet count (thrombocytopenia) or ITP (idiopathic thrombocytopenic purpura)',
  })
  hadBDSpleen: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDSpleenDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    comment: 'Blood Clotting Disorders (easy or excessive Bleeding) or Coagulation Defects (Hemophilia)',
  })
  hadBloodClotDisorderOrCoagulationDefects: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBloodClotOrCoagulationDefectsDiagnosis: DiagnosisShort;

  @property({
    comment: 'Problems with blood clotting, easy or excessive bleeding, or coagulation defects (hemophilia)',
  })
  hadBDClotting: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDClottingDiagnosis: DiagnosisShort;

  @property()
  hadBDHemochromatosis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDHemochromatosisDiagnosis: DiagnosisShort;

  @property({ comment: 'Blood disorder - Other/Not Sure' })
  hadBDOther: boolean;

  // TODO: fix this typo
  @property({ type: Array, getType: () => Diagnosis })
  otherDiagnosisis: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A disorder of the brain, spinal cord or nervous system including seizures, tremors, paralysis, dizziness, fainting, headaches, brain tumor, brain aneurysm or bleeding, stroke or TIA (transient ischemic attack)',
  })
  hadBrainDisorder: boolean;

  @property({ comment: 'Brain aneurysm or bleeding' })
  hadBDAneurysm: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBDAneurysmDiagnosis: DiagnosisShort;

  @property({ comment: 'Brain tumor' })
  hadDBTumor: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBTumorDiagnosis: DiagnosisShort;

  @property({ comment: 'Encephalitis or meningitis' })
  hadDBEncephalitits: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Did you recover and did your doctor say you are neurologically normal now',
  })
  hasDBRecovered: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Did you fully recover from this episode more than 2 years ago',
  })
  hasDBRecovered2Years: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBEncephalititsDiagnosis: DiagnosisShort;

  @property({ comment: 'Epilepsy or seizures' })
  hadDBSeizures: boolean;

  @property({
    rule: ['required'],
    values: ['None', 'One', 'Morethanone'],
    descriptions: { None: 'None', One: 'One', Morethanone: 'More than one' },
    comment: 'How many medications do you currently take for your seizures',
  })
  medsDBSeizures: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Was your last seizure more than 5 years ago',
  })
  hadDBSiezuresLast5Years: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBSeizuresDiagnosis: DiagnosisShort;

  @property({ comment: 'Headaches or migraines' })
  hadDBMigranes: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you received disability payments within the past 12 months for your headaches/migraines',
  })
  hadDBMDisabilityPayments: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBMDisabilityPaymentsDiagnosis: DiagnosisShort;

  @property({ comment: 'Multiple Sclerosis' })
  hadDBMS: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBMSDiagnosis: DiagnosisShort;

  @property({ comment: 'Neuropathy (Chronic or Inflammatory)' })
  hadDBNeuropathy: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBNeuropathyDiagnosis: DiagnosisShort;

  @property({ comment: "Parkinson's Disease" })
  hadDBParkinsons: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBParkinsonsDiagnosis: DiagnosisShort;

  @property()
  hadDBSciatica: boolean;

  @property()
  hadDBStroke: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBStrokeDiagnosis: DiagnosisShort;

  @property({ comment: 'Fainting (Syncope)' })
  hadDBFainting: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Have you had more than one episode of fainting',
  })
  hadDBFMultipleEpisodes: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBFMultipleEpisodesDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Was your most recent episode of fainting more than one year ago',
  })
  hadDBFLastYear: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBFLastYearDiagnosis: DiagnosisShort;

  @property({ comment: 'Transient Ischemic Attack (TIA)' })
  hadDBTIA: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBTIADiagnosis: DiagnosisShort;

  @property({ comment: 'Subdural Hematoma / Hematoma in the brain' })
  hadDBHematoma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDBHematomaDiagnosis: DiagnosisShort;

  @property({ comment: 'Traumatic brain injury' })
  hadDBTBI: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Did the injury occur more than 5 years ago',
  })
  hadDBTBILast5Years: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Do you currently have any symptoms or take any medication related to this injury',
  })
  hasTBISymptomsOrMeds: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasTBISymptomsOrMedsDiagnosis: DiagnosisShort;

  @property({ comment: "Tourette's syndrome" })
  hadTBTourettes: boolean;

  @property({ comment: 'Brain disorder - Other/Not Sure' })
  hadTBNotSure: boolean;

  @property({ comment: 'Emphysema, COPD' })
  hadSeriousRespiratoryDisorder: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Cirrhosis of the liver',
  })
  hadCirrhosis: boolean;

  @property({ comment: 'Other condition of the kidney or brain' })
  hadKidneyOrBrainConditionOther: boolean;

  @property({ comment: 'Terminal illness (fewer than 12 months to live)' })
  hadTerminalIllness: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  diagnosisTB: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Depression, anxiety, nervousness, stress, psychosis, suicide thoughts or attempts, anorexia or bulimia, post traumatic stress disorder, obsessive compulsive disorder, bipolar disorder, attention deficit hyperactivity disorder (ADHD) or other emotional disorder',
  })
  hadEmotionalDisorder: boolean;

  @property({ comment: 'Psychosis' })
  hadEOPsychosis: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'yes', false: 'no' },
    comment: 'Hospitalized or recommended hospitalization for psychosis',
  })
  hadEOPsychosisHospitalizedLast10Years: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOPsychosisHospitalizedDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      'In the past 5 years, have you been hospitalized or been enrolled in a partial hospitalization due to an emotional disorder',
  })
  hadEOHospitalizedLast5Years: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOHospitalizedLast5YearsDiagnosis: DiagnosisShort;

  @property()
  hadEOAlcoholism: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOAlcoholismDiagnosis: DiagnosisShort;

  @property({ comment: 'Anxiety or Depression' })
  hadEOADepression: boolean;

  @property({ comment: 'Select anxiety or depression' })
  hadEOADepressionType: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Been to the ER, been hospitalized, or been recommeneded hospitalization for anxiety or depression in the last 10 years',
  })
  hadEOADepressionHospitalizedLast10Years: boolean;

  @property({
    rule: ['required'],
    comment:
      'Details for Been to the ER, been hospitalized, or been recommeneded hospitalization for anxiety or depression in the last 10 years',
  })
  hadEOADepressionHospitalizedDetails: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOADepressionHospitalizedDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently undergoing treatment',
  })
  hadEOTreatment: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have there been any changes in the dosage of your treatment',
  })
  hadChangeDoseEOTreatment: boolean;

  @property({ comment: 'Information about changes in treatment' })
  hadChangeDoseEOATreatmentOther: string;

  @property({ comment: 'Details about changes in treatment' })
  hadChangeDoseEOATreatmentDetails: string;

  @property({ comment: 'Diagnosis details about changes in treatment', getType: () => DiagnosisShort })
  hadChangeDoseEOATreatmentDiagnosis: DiagnosisShort;

  @property({ type: Array, getType: () => RX })
  medsEOADepression: Array<RX> = [];

  @property({ comment: 'Details about treatment', getType: () => DiagnosisShort })
  hadEOADepressionOther: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your anxiety or depression situational only',
  })
  hadEODepressionSituationalOnly: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Does your anxiety affect your social or work activities in any way',
  })
  hadEODepressionSocialSituational: boolean;

  @property({ rule: ['required'], comment: 'Details about how your anxiety affects your social or work activities' })
  hadEODepressionSocialSituationalDetails: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEODepressionSocialSituationalDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOADepressionDiagnosis: DiagnosisShort;

  @property({
    rule: ['required', 'shortdate'],
    comment: 'Date of depression diagnossis',
  })
  hadEOADepressionDiagnosisDate: Date;

  @property({
    values: ['medication', 'counseling', 'medicationAndCounseling', 'none'],
    descriptions: { medication: 'Medication', counseling: 'Counseling', medicationAndCounseling: 'Both', none: 'None' },
    comment: 'Undergoing any form of treatment (counseling or medication)',
  })
  hadEOADepressionTreatmentStr: string;

  @property({ comment: 'Name of doctor that treated depression' })
  hadEOADepressionTreatmentDoctor: string;

  @property({
    rule: ['required'],
    values: ['lastvisit', 'unknown'],
    descriptions: { lastvisit: '__ Date', unknown: 'Unknown' },
    comment: 'Last visit to the Doctor or Therapist',
  })
  hadEOADepressionDoctorLastVisitWhen: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Symptoms have been well controlled by medication for at least 6 months',
  })
  hadEOADepression6MonthsSymptomsControlled: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you suffer from panic or stress induced chest pains or heart palpitations',
  })
  hadEOHeartPalpitations: boolean;

  @property({
    rule: ['required'],
    values: ['less_than_one', 'one_to_five', 'more_than_five'],
    descriptions: { less_than_one: 'Less than 1', one_to_five: '1 to 5', more_than_five: 'more than 5' },
    comment: 'Years since diagnosed with emotional disorder',
  })
  hadEODiagnosisDate: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you suffer from panic or stress induced chest pains or heart palpitations',
  })
  hadCare4EOHeartPalpitations: boolean;

  @property({ comment: 'Attention Deficit Hyperactivity Disorder (ADHD)' })
  hadEOADHD: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Did your ADHD Symptoms begin in childhood (before age 10)',
  })
  hadEOADHDChildhood: boolean;

  @property({ comment: "Cognitive Impairment (Alzheimer's, Dementia, etc.)" })
  hadEO: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEODiagnosis: DiagnosisShort;

  @property()
  hadEODelusion: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { 'true': 'Yes', 'false': 'No', 'Not Sure': 'Not Sure' },
    comment: 'Hospitalized or recommended hospitalization for Delusion in the last 10 years',
  })
  hadEODelusionHospitalizedLast10Years: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEODelusionHospitalizedLast10YearsDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEODelusionDiagnosis: DiagnosisShort;

  @property({ comment: 'Drug Counseling' })
  hadEODrugCounseling: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEODrugCounselingDiagnosis: DiagnosisShort;

  @property({ comment: 'Eating Disorder' })
  hadEOEatingDisorder: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOEatingDisorderDiagnosis: DiagnosisShort;

  @property({
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Hospitalized or recommended hospitalization for EatingDisorder',
  })
  hadEOEatingDisorderHospitalizedLast10Years: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOEatingDisorderHospitalizedDiagnosis: DiagnosisShort;

  @property({ comment: 'Post Traumatic Stress Disorder' })
  hadEOPTSD: boolean;

  @property({
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Hospitalized or recommended hospitalization for PTSD',
  })
  hadEOPTSDHospitalizedLast10YearsStr: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOPTSDHospitalizedLast10YearsDiagnosis: DiagnosisShort;

  @property({ comment: 'Obsessive Compulsive Disorder (OCD)' })
  hadEOOCD: boolean;

  @property({
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Hospitalized or recommended hospitalization for OCD',
  })
  hadEOOCDHospitalizedLast10YearsStr: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOOCDHospitalizedLast10YearsDiagnosis: DiagnosisShort;

  @property({ comment: 'Bipolar Disorder' })
  hadEOBipolar: boolean;

  @property({
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Hospitalized or recommended hospitalization for Bipolar',
  })
  hadEOBipolarHospitalizedLast10YearsStr: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOBipolarHospitalizedDiagnosis: DiagnosisShort;

  @property({ comment: 'Major Depression' })
  hadEOMajorDepression: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOMajorDepressionDiagnosis: DiagnosisShort;

  @property({ comment: 'Panic Attack' })
  hadEOPanicAttack: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you received medical care or been prescribed medication for panic attacks within the past 2 years',
  })
  hadEOPAMedsLast2Years: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOPAMedsLast2YearsDiagnosis: DiagnosisShort;

  @property({ comment: 'Suicide Attempt' })
  hadEOSuicideAttempt: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEOSuicideAttemptDiagnosis: DiagnosisShort;

  @property({ comment: 'Hospitalized or recommended hospitalization for Suicide Attempt within the past 10 years' })
  hadEOSuicideAttemptHospitalizedLast10Years: string;

  @property({
    comment:
      'Brief Stress or Grief Reaction, Performance Anxiety (public speaking, etc.), Seasonal Affective Disorder, Social Phobia, Simple Phobia (fear of snakes, etc.)',
  })
  hadEOMinorDisorders: boolean;

  @property({ comment: 'Emotional disorder - Other/Not Sure' })
  hadEONotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  diagnosisEONotSure: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A disorder of the eyes, ears, nose, throat or sinuses including any partial or complete loss of hearing, vision or speech',
  })
  hadEENT: boolean;

  @property({ comment: 'Acoustic Neuroma' })
  hadEENTAcousicNeuroma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEENTAcousicNeuromaDiagnosis: DiagnosisShort;

  @property()
  hadEENTLeukoplakia: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEENTLeukoplakiaDiagnosis: DiagnosisShort;

  @property({ comment: 'Optic Neuritis' })
  hadEENTOpticNeuritis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadEENTOpticNeuritisDiagnosis: DiagnosisShort;

  @property({ comment: 'EENT - Other/Not Sure' })
  hadEENTNotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  diagnosisEENTNotSure: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Asthma, allergies, shortness of breath, bronchitis, emphysema, chronic obstructive pulmonary disease (COPD), pneumonia, sleep apnea, tuberculosis or any other disorder of the respiratory system',
  })
  hadRespiratoryDisorder: boolean;

  @property()
  hadRDSarcoidosis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDSarcoidosisDiagnosis: DiagnosisShort;

  @property({ comment: 'Chronic Obstructive Pulmonary Disease (COPD)' })
  hadRDCOPD: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDCOPDDiagnosis: DiagnosisShort;

  @property()
  hadRDEmphysema: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDEmphysemaDiagnosis: DiagnosisShort;

  @property()
  hadRDAsthma: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'has had symptoms of asthma occurring frequently',
  })
  hadRDAsthmaSymptomsFrequently: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you been hospitalized (kept overnight in the hospital) in the past year',
  })
  hadRDAHospitalizedLast1Year: boolean;

  @property({ rule: ['required', 'numericPositive'], comment: 'number of times been hospitalized for asthma' })
  numberHospitalizationsRDAsthmaRelated: number;

  @property({ rule: ['date', 'pastDate', 'required'], comment: 'Date of last hospitalization for asthma' })
  lastRDAsthmaHospitalization: Date;

  @property({
    rule: ['required'],
    comment: 'Diagnosis details of Emergency visit for Asthma in last one year',
    getType: () => DiagnosisShort,
  })
  hadRDAHospitalizedLast1YearDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hadRDAERLast1Year: boolean;

  @property({
    rule: ['required', 'numericPositive'],
    comment: 'Have you visited the emergency department in the past year for your asthma',
  })
  hadRDAERLast1YearNumberOfTimes: number;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDAERLast1YearDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 2 years has your asthma required an emergency care visit to a hospital, emergency room or urgent care center?',
  })
  hadRDAER: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently taking daily steroid pills, such as prednisone, for your asthma',
  })
  hasRDAMeds: boolean;

  @property({
    rule: ['required', 'numericPositive'],
    comment: 'How many times in the past 12 months have you been prescribed oral steriod pills',
  })
  howManyRDAMeds: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'has been prescribed daily steroid pills',
  })
  hasRDAMedsNow: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasRDAMedsDiagnosis: DiagnosisShort;

  @property({ type: Array, getType: () => RX, comment: 'List of medications taken for Asthma' })
  medsRDAsthma: Array<RX> = [];

  @property({ comment: 'Pulmonary Nodule' })
  hadRDPulmonaryNodule: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDPulmonaryNoduleDiagnosis: DiagnosisShort;

  @property({ comment: 'Sleep Apnea' })
  hasRDApnea: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasRDApneaDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently being treated with CPAP',
  })
  hasRDCPAP: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you been using CPAP for more than 6 months',
  })
  hasRDCPAPLast6Months: boolean;

  @property()
  hasRDBronchitis: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Have you fully recovered from Bronchitis',
  })
  hasRDBRecovered: string;

  @property({ comment: "Pneumonia or Legionnaire's disease" })
  hadRDPneumonia: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Did you fully recover from Pneumonia without complications',
  })
  hasRDPRecovered: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasRDPRecoveredDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasRDBRecoveredDiagnosis: DiagnosisShort;

  @property()
  hadRDTuberculosis: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Have you fully recovered without complications and are you no longer taking medications for tuberculosis',
  })
  hasRDTRecovered: string;

  @property({ rule: ['required'], comment: 'Which medications do you take' })
  medications: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDTuberculosisDiagnosis: DiagnosisShort;

  @property({ comment: 'Influenza Intermittent Cough/Cold Seasonal or Perennial Allergies' })
  hadRDInfluenza: boolean;

  @property({ comment: 'Respiratory disorder - Other/Not sure' })
  hadRDOther: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  diagnosisRDOther: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A disorder of the digestive system, liver, pancreas or gall bladder including hepatitis, jaundice, ulcers, intestinal bleeding, colitis, Crohn’s disease (ileitis), polyps, recurrent indigestion, diarrhea or diverticulitis',
  })
  hadDigestiveDisorder: boolean;

  @property({ comment: 'Anorectal fistula or sinus' })
  hadDDAnorectalFistula: boolean;

  @property({
    rule: ['required'],
    comment: 'Diagnosis details of Anorectal fistula or sinus',
    getType: () => DiagnosisShort,
  })
  hadDDAnorectalFistulaDiagnosis: DiagnosisShort;

  @property({ comment: 'Celiac Disease' })
  hadDDCeliacDisease: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDCeliacDiseaseDiagnosis: DiagnosisShort;

  @property({ comment: 'Colon polyp' })
  hadDDColonPolyp: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are your celiac disease symptoms controlled with dietary restrictions (no medications)',
  })
  hasMedicationCeliacDisease: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDColonPolypDiagnosis: DiagnosisShort;

  @property({ comment: "Crohn's disease" })
  hadDDChrons: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDChronsDiagnosis: DiagnosisShort;

  @property({ comment: 'Colitis (non ulcerative)' })
  hadDDColitusNonUlcerative: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDColitusNonUlcerativeDiagnosis: DiagnosisShort;

  @property({ comment: 'Colitis (ulcerative)' })
  hadDDColitusUlcerative: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDColitusUlcerativeDiagnosis: DiagnosisShort;

  @property()
  hadDDiverticulitis: boolean;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hadDDDLast6Months: boolean;

  @property({
    rule: ['required'],
    comment: 'Have you had an episode of Diverticulitis in the past 6 months',
    getType: () => DiagnosisShort,
  })
  hadDDDLast6MonthsDiagnosis: DiagnosisShort;

  @property({ comment: 'Non-alcoholic fatty liver disease (NAFLD) or nonalcoholic steatohepatitis (NASH)' })
  hadDDNAFLD: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDNAFLDDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you ever been advised to have a biopsy of your liver',
  })
  hadDDLiverBiopsy: boolean;

  @property()
  hadDDGastritis: boolean;

  @property({ rule: ['required'], comment: 'Diagnosis details of Gastritis', getType: () => DiagnosisShort })
  hadDDGastritisDiagnosis: DiagnosisShort;

  @property({ comment: 'Hepatitis B, C, or D' })
  hadDDHepititis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDHepititisDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBMDBackPainMedsDiagnosis: DiagnosisShort;

  @property({ comment: 'Pancreatic abscess or cysts' })
  hadDDPancreaticInflamation: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDPancreaticInflamationDiagnosis: DiagnosisShort;

  @property({ comment: 'Pancreatitis (pancreatic inflammation)' })
  hadDDPancreatitis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDPancreatitisDiagnosis: DiagnosisShort;

  @property()
  hadDDUlcer: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Is/are your ulcer(s) completely healed',
  })
  hadDDURecovered: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDURecoveredDiagnosis: DiagnosisShort;

  @property({ comment: 'Weight Loss Surgery' })
  hadDDWeightLossSurgery: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadDDWeightLossSurgeryDiagnosis: DiagnosisShort;

  @property({
    comment:
      'Anorectal Abscess or Cyst, Appendicitis, Diverticulosis, Food Intolerance, Food Allergy, Gallbladder Pain, Gallstones, Gastroesophageal Reflux Disease (GERD), Hemorrhoids, Hepatitis A or E (fully recovered), Hiatal Hernia, High Levels of Bilirubin (Hyperbilirubinemia), Inguinal (Groin) Hernia, Intermittent or Chronic Constipation, Intermittent Diarrhea, Irritable Bowel Syndrome or Irritable Bowel Disease (IBS), Rectal Prolapse',
  })
  hadDDBenign: boolean;

  @property({ rule: ['required'], comment: 'Diagnosis details of Benign', getType: () => DiagnosisShort })
  hadDDBenignDiagnosis: DiagnosisShort;

  @property({ comment: 'Blood disorder - Other/Not sure' })
  hadBDNotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  diagnosisDDOther: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A disorder or impairment of the muscles, bones, joints, nerves, spine, neck or back including arthritis, gout, sciatica or amputations',
  })
  hadBoneMuscularDisorder: boolean;

  @property({ comment: 'Amputations (due to a trauma only)' })
  hadBMDAmputationsFromTrauma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBMDAmputationsFromTraumaDiagnosis: DiagnosisShort;

  @property({ comment: 'Arthritis (including rheumatoid arthritis, osteoarthritis, and polyarthritis)' })
  hadBMDArthritis: boolean;

  @property({
    rule: ['required'],
    values: ['Osteoarthritis', 'RheumatoidArthritis', 'Polyarthritis', 'PolyinflammatoryArthritis', 'Other/NotSure'],
    descriptions: {
      'Osteoarthritis': 'Osteoarthritis',
      'RheumatoidArthritis': 'Rheumatoid Arthritis',
      'Polyarthritis': 'Polyarthritis',
      'PolyinflammatoryArthritis': 'Polyinflammatory Arthritis',
      'Other/NotSure': 'Other / Not Sure',
    },
    comment: 'What type of arthritis do you have',
  })
  hadBMDAType: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadBMDATypeDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you take any prescription medication for your arthritis',
  })
  hasBMDMeds: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBMDMedsDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you currently receive disability benefits for your arthritis',
  })
  hasBMDDisabilityBenefits: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBMDDisabilityBenefitsDiagnosis: DiagnosisShort;

  @property({ comment: 'Osteoporosis or osteopenia' })
  hasBMDOsteoporosis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBMDOsteoporosisDiagnosis: DiagnosisShort;

  @property({ comment: 'Back pain' })
  hasBMDBackPain: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently taking prescription medication for your back pain',
  })
  hasBMDBPMeds: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently undergoing physical therapy related to your back pain',
  })
  hasMedicationBMDBackPain: boolean;

  @property({ comment: "Paget's disease of the bone" })
  hasBMDPagets: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasBMDPagetsDiagnosis: DiagnosisShort;

  @property({
    comment:
      "Bone Fractures Due to Trauma, Bursitis, Gout, Joint Laxity/Hypermobility, Overuse Injury (Stress Fracture, Shin Splints, Etc.), Scoliosis, Tendonitis/Tendonosis, Tennis/Golfer's Elbow (Epicondylitis), Rotator Cuff Problems, Patellofemoral Pain/Chondromalacia Patella",
  })
  hasBMDBenign: boolean;

  @property({ comment: 'Bone muscular disorder - Other/ Not Sure' })
  hasBMDOther: boolean;

  // TODO: fix this typo
  @property({ type: Array, getType: () => Diagnosis })
  diagosisBMDAOther: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Epstein-Barr virus, Lyme disease, chronic fatigue syndrome, fibromyalgia, lupus or other rheumatologic disorder',
  })
  hadRhumatologicDisorder: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Have you been prescribed ongoing treatment with steroids or immunosuppressants for your connective tissue disease?',
  })
  hadRheumaticDisorderTreatment: boolean;

  @property({ comment: 'Chronic fatigue syndrome or fibromyalgia' })
  hadRDChronicFatigue: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Are you able to function normally',
  })
  hasRDCFFunctionNormal: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Do you suffer from depression',
  })
  hadRDDepression: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDChronicFatigueDiagnosis: DiagnosisShort;

  @property({ comment: 'Epstein-Barr virus' })
  hadRDEpsteinBarr: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Do you currently suffer from fatigue or other symptoms related to your virus',
  })
  hadRDEBOtherSymptoms: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDEBOtherSymptomsDiagnosis: DiagnosisShort;

  @property({ comment: 'Systemic Lupus Erythematosus (SLE)' })
  hadRDLupus: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadRDLupusDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Lyme disease',
  })
  hadRDLymeDisease: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Do you currently have fatigue or any other symptoms associated with your Lyme disease',
  })
  hadRDLDSymptoms: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently receiving treatment related to Lyme disease',
  })
  hasRDLDTreatment: boolean;

  @property({ comment: 'Rheumatologic disorder - Other/Not Sure' })
  hasRDOther: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  diagnosisRMDOther: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Diabetes or a disorder of the thyroid, pituitary or adrenal glands',
  })
  hasDiabetesThyroid: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Is your Hypothyroid condition treated with medication and stable',
  })
  hasMedicationHypothyroid: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Have you been advised to have periodic imaging or biopsy of a thyroid cyst, nodule or tumor',
  })
  hasBiopsyThyroid: string;

  @property({ comment: "Under- or overactive thyroid (hypo- or hyperthyroidism), Grave's or Hashimoto's disease" })
  hasHypothyroid: boolean;

  @property()
  hasDTDiabetes: boolean;

  @property({
    rule: ['required'],
    values: ['Type1', 'Type2', 'Gestational', 'Other/NotSure'],
    descriptions: {
      'Type1': 'Type 1',
      'Type2': 'Type 2',
      'Gestational': 'Gestational',
      'Other/NotSure': 'Other/Not Sure',
    },
    comment: 'What type of diabetes do/did you have',
  })
  hasDTDType: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your diabetes treated with insulin?',
  })
  hasDTDiabetesInsulin: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Have you had diabetic complications such as nerve pain,  skin ulcers, vision problems or kidney problems?',
  })
  hasDTDiabetesComplications: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Has it been more than 5 years since your last pregnancy with gestational diabetes',
  })
  hasPregnancyGestational: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  diabetesDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasHypothyroidDiagnosis: DiagnosisShort;

  @property()
  hasDTProlactinoma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasDTProlactinomaDiagnosis: DiagnosisShort;

  @property({ comment: "Thyroid disease (cysts, nodules, thyroidectomy, Grave's, Hashimoto's, etc.)" })
  hasDTThyroidDisease: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Is your thyriod condition treated and stable',
  })
  hasDTTStable: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasDTTStableDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Have you been advised to have periodic imaging or biopsy of a thyroid cyst, nodule or tumor',
  })
  hasDTTImageBiopsy: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasDTTImageBiopsyDiagnosis: DiagnosisShort;

  @property({ comment: 'Diabetes/Thyroid - Other/Not Sure' })
  hasDTNotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  DiagnosisisDTNotSure: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A disorder of the kidneys, bladder, prostate or urinary tract or findings of sugar, protein or blood in the urine',
  })
  hadUrninaryTrackDisorder: boolean = false;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      'To the best of your knowledge and belief, does your anxiety affect your social or work activities in any way',
  })
  hadUrninaryTrackDisorderDialysis: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUrninaryTrackDisorderDiagnosis: DiagnosisShort;

  @property({ isLocal: true })
  private _hadDialysis: boolean;

  @property({ isLocal: true })
  get hadDialysis(): boolean {
    return this.hadUrninaryTrackDisorderDialysis === 'Yes' || this.hadUrninaryTrackDisorderDialysis === 'NotSure';
  }

  set hadDialysis(x: boolean) {
    this.hadUrninaryTrackDisorderDialysis = x ? 'Yes' : 'No';
  }

  @property({ comment: 'Acute nephritis, within the past 2 years' })
  hadNephritisLast2Years: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadNephritisLast2YearsDiagnosis: DiagnosisShort;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadFullTestExclHIVDiagnosis: DiagnosisShort;

  @property({ comment: 'Acute Nephritis, more than 2 years ago' })
  hadNephritisMoreThanLast2Years: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadNephritisMoreThanLast2YearsDiagnosis: DiagnosisShort;

  @property({ comment: 'Bladder stones (calculi)' })
  hadUTDBladderStones: boolean;

  @property({ comment: 'Glomerulonephritis or Nephropathy' })
  hadUTDGlomerulonephiritis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTDGlomerulonephiritisDiagnosis: DiagnosisShort;

  @property({ comment: 'Kidney stones' })
  hadUTDKidneyStones: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you had kidney stones more than once',
  })
  hadUTDKidneyStonesMultiple: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Do you currently have normal kidney function without any obstruction or infection',
  })
  hasUTDNormalKidney: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasUTDNormalKidneyDiagnosis: DiagnosisShort;

  @property({ comment: 'Kidney failure / renal insufficiency / dialysis' })
  hadUTDRenalFailure: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTDRenalFailureDiagnosis: DiagnosisShort;

  @property({ comment: 'Kidney infection (pyelonephritis)' })
  hadUTDPyelonephiritis: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      'Do you have any known issues with your urological anatomy (for example: strictures or an abnormal ureter)',
  })
  hadUTStructural: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTStructuralDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Was your kidney infection treated and cured more than 3 months ago',
  })
  hadUTDKidneyRecovered3Months: string;

  @property({ comment: 'Proteinuria (protein in the urine on more than one occasion)' })
  hadUTDProteinuria: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTDProteinuriaDiagnosis: DiagnosisShort;

  @property({ comment: 'Renal cysts' })
  hadUTDRenalCyst: boolean;

  @property({
    rule: ['required'],
    values: ['Single', 'Multiple', 'NotSure'],
    descriptions: { Single: 'Single', Multiple: 'Multiple', NotSure: 'Not Sure' },
    comment: 'Do you have a single cyst or multiple cysts',
  })
  hadUTDRCMultiple: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTDRCMultipleDiagnosis: DiagnosisShort;

  @property({ comment: 'Elevated PSA' })
  hadUTDElevatedPSA: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTDElevatedPSADiagnosis: DiagnosisShort;

  @property({ comment: 'Interstitial Cystitis' })
  hadUTDInterstitialCystitis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadUTDInterstitialCystitisDiagnosis: DiagnosisShort;

  @property({
    comment:
      'Childhood kidney reflux (vesicoureteral reflux), Congenital solitary kidney, horseshoe kidney, Enlarged Prostate Gland/Prostatic Hyperplasia, Incontinence, Prostatitis, Urinary Tract Infection or Cystitis',
  })
  hadUTDBenign: boolean;

  @property({ comment: 'Urinary tract disorder - Other/Not sure' })
  hadUTDNotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  DiagnosisUTDNotSure: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'A disorder of the skin including eczema or psoriasis',
  })
  hadSkinDisorder: boolean = false;

  @property()
  hadSDPsoriasis: boolean;

  @property({ comment: 'Do you have arthritis or psoriatic arthritis' })
  hasEczema: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
  })
  hadSDArthritis: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadSDArthritisDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you undergoing any skin treatment other than the use of topical medications',
  })
  hasSDMeds: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hasSDMedsDiagnosis: DiagnosisShort;

  @property({ comment: 'Skin disorder - Other/Not sure' })
  hasSDOther: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  DiagnosisSDNotSure: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A diagnosis of Human Immunodeficiency Virus (AIDS virus) infection or Acquired Immune Deficiency Syndrome (AIDS)',
  })
  hadImmunoDeficiency: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadImmunoDeficiencyDiagnosis: DiagnosisShort;

  @property({ comment: 'had Myeloma' })
  hadMyeloma: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadMyelemoDiagnosis: DiagnosisShort;

  @property({ comment: 'MGUS (Monoclonal Gammopathy of Undetermined Significance)' })
  hadMGUS: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadMGUSDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'A disorder of the uterus, cervix, ovaries or breasts',
  })
  hadIDReproductive: boolean;

  @property({ comment: 'Cervicitis, endometriosis, or pelvic inflammatory disease' })
  hadIDCervicitis: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadIDCervicitisDiagnosisDiagnosis: DiagnosisShort;

  @property({ comment: 'Abnormal Uterine Bleeding' })
  hadAbnormalUterineBleeding: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadAbnormalUterineBleedingDiagnosis: DiagnosisShort;

  @property({ comment: 'Preeclampsia or Eclampsia' })
  hadCPPreclampsia: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCPPreclampsiaDiagnosis: DiagnosisShort;

  @property({ comment: 'HELLP Syndrome' })
  hadCPHELLP: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCPHELLPDiagnosis: DiagnosisShort;

  @property({ comment: 'Fibrocystic disease of the breast' })
  hadIDFibrocystic: boolean;

  @property({ comment: 'Ovarian cyst' })
  hadIDOvarianCyst: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Is Ovarian cyst benign',
  })
  hadIDOBenign: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadIDOBenignDiagnosis: DiagnosisShort;

  @property({ comment: 'Polycystic ovarian syndrome' })
  hadIDPolycystic: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadIDPolycysticDiagnosis: DiagnosisShort;

  @property({
    comment:
      'Congenital malformation of the genital tract, Ovarian Torsion, Ectopic Pregnancy, Papilloma of the Female Genital Tract, Sexually Transmitted Disease (other than HIV or Syphilis), Uterine Fibroid, Uterine Prolapse',
  })
  hadIDCongenitalMalform: boolean;

  @property({ comment: 'Disorder of the uterus, cervix, ovaries or breasts - Other/Not sure' })
  hadIDNotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  DiagnosisIDNotSure: Array<Diagnosis> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Complicated Pregnancy',
  })
  hadComplicatedPregnancy: boolean;

  @property({
    comment:
      'Was your complicated pregnancy due to Multiple Miscarriages, Placental Problems, Bleeding or Labor/Delivery Issues',
  })
  hadCPBenign: boolean;

  @property({ comment: 'Complicated pregnancy: High Blood Pressure' })
  hadCPHighBloodPressure: boolean;

  @property({ comment: 'Complicated pregnancy: Heart, Liver or Kidney Problems' })
  hadCPHeartLiverKidneyProblems: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCPHeartLiverKidneyProblemsDiagnosis: DiagnosisShort;

  @property({ comment: 'Complicated pregnancy: Seizures' })
  hadCPSeizures: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCPSeizuresDiagnosis: DiagnosisShort;

  @property({ comment: 'Complicated pregnancy: Gestational Diabetes' })
  hadCPGestationalDiabities: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadCPGestationalDiabitiesDiagnosis: DiagnosisShort;

  @property({ comment: 'Complicated pregnancy - Other/Not Sure' })
  hadCPNotSure: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  CPDetails: Array<Diagnosis> = [];

  @property({ comment: 'No health conditions' })
  hadNoCondition: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 10 years, have you: Used cocaine, barbiturates, amphetamines, heroin, narcotics, stimulants, hallucinogens or other controlled substances or habit forming drugs not prescribed by a physician',
  })
  hadDrugUse: boolean;

  @property({ comment: 'Drug use details' })
  hadDrugUseDetails: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Received medical treatment, attended a program or been counseled for alcohol or drug abuse or been advised by a member of the medical profession to reduce the use of alcohol',
  })
  hadAlcoholAbuse: boolean;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadAlcoholAbuseDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Had any of the following medical procedures:A blood transfusion',
  })
  hadBloodTransfusion: boolean;

  @property({ type: Array, getType: () => Surgery })
  bloodTransfusionDetails: Array<Surgery> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'A surgical procedure involving your heart, arteries or veins such as a stent implant angioplasty, pacemaker implant or ablation',
  })
  hadHeartSurgery: boolean;

  @property({ type: Array, getType: () => Surgery })
  heartSurgeryDetails: Array<Surgery> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'A prostatectomy',
  })
  hasProstatectomy: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'A mastectomy or oophorectomy',
  })
  hadMastectomy: boolean;

  @property({ type: Array, getType: () => Surgery })
  otherSurgeryDetails: Array<Surgery> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'In the past 5 years, have you: Had an ECG (electrocardiogram)',
  })
  hadECG: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Was your ECG result normal',
  })
  hadECGNormal: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Was the ECG part of a routine physical or check-up',
  })
  hadECGCheckup: string;

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadECGNormalDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Had an application for life, disability or health insurance declined, postponed, rated or restricted',
  })
  hadInsuranceRestricted: boolean;

  @property({ comment: 'Insurance restricted details' })
  hadInsuranceRestrictedDetails: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Had a sickness or injury for which a disability claim was made or payments, benefits or pension benefits were received',
  })
  hadDisabilityClaim: boolean;

  @property({ comment: 'Disability claim details' })
  hadDisabilityClaimDetails: string;

  @property({ comment: 'None of the Above' })
  hadNoECGDL: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 3 years, have you: Had a physical exam, check-up, or evaluation by a member of the medical profession regarding a condition not previously stated on this application?i',
  })
  hadExamOther: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      'Were all these exams, check-ups and/or evaluations performed for routine medical care (routine physicals, immunizations, routine gynecological care, etc.), acute limited illness (cough, cold, flu, sore throat, sinus infection, rashes, urinary infection, etc.), or minor musculoskeletal injury/pain (abrasions, contusions, lacerations, back/neck or joint injury, sprains, strains, etc.?',
  })
  hadEORoutine: string;

  @property({
    rule: ['required'],
    comment:
      'Were all these exams, check-ups and/or evaluations performed for routine medical care (routine physicals, immunizations, routine gynecological care, etc.), acute limited illness (cough, cold, flu, sore throat, sinus infection, rashes, urinary infection, etc.), or minor musculoskeletal injury/pain (abrasions, contusions, lacerations, back/neck or joint injury, sprains, strains, etc.',
  })
  hadEORoutineDetails: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      'Been advised by a member of the medical profession to have surgery, medical treatment, or diagnostic testing excluding HIV testing, which has not been completed',
  })
  hadFullDiagnosisExclHIV: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Are you currently under treatment by a member of the medical profession for anything not previously stated on this application',
  })
  hadTreatmentOther: boolean;

  @property({ type: Array, getType: () => Diagnosis })
  hadTreatmentOtherDiagnosisCollection: Array<Diagnosis> = [];

  @property({ rule: ['required'], getType: () => DiagnosisShort })
  hadTreatmentOtherDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Are you currently taking any prescription medication not previously stated on this application (excluding contraceptives)',
  })
  hadRXOther: boolean;

  @property({ type: Array, getType: () => RX })
  hadMedsOther: Array<RX> = [];

  @property({
    rule: ['required'],
    values: function () {
      const iState = this.customer.application.primaryPolicy.insured.address.state;
      const oState = this.customer.application.primaryPolicy.ownerPersonResidentialAddress.state;
      const havePrimaryPhysician = this.customer.application.primaryPolicy.insured.personalHistory.primaryPhysician;
      return HIVStateOptions.getOptions(
        iState,
        oState || iState,
        havePrimaryPhysician,
        this.customer.application.primaryPolicy.getChannelCode(),
        this.customer.application.primaryPolicy.product
      );
    },
    descriptions: {
      insured_address: "Insured's Address",
      pp_address: "Primary Physician's Address",
      insured_another_address: 'Insured Address not stated above',
      pp_another_address: 'Physician Address not stated above',
      local_health: 'Local Department of Health',
      state_health: 'State Department of Health',
      optout: 'I do not wish to be tested for HIV',
      other_healthcare: 'Other Healthcare Provider',
      other: 'Other',
      noneoftheabove: 'None of the Above',
    },
    comment: 'In the case of a positive HIV test result, I would like the results sent to:',
  })
  hivPositiveWhichAddress: string;

  @property({
    rule: ['required'],
    validate: 'isNotEmptyAndMinLength(2)',
    comment: 'HIV results sent to: Physician name',
  })
  hivPositiveAddressPPName: string;

  @property({
    rule: ['required'],
    validate: 'isNotEmptyAndMinLength(2); isMaxLength(88)',
    comment: 'HIV results sent to: Address (Line1, Line2, City, State, Zip)',
  })
  hivPositiveAddress: string;

  @property({
    rule: ['required'],
    comment: 'HIV results sent to: Address (Line1, Line2, City, State, Zip)',
    getType: () => Address,
  })
  hivPositiveAddressTerm2: Address;

  @property()
  medicalConditionCount: number;

  @property({ rule: 'shortdate' })
  primaryPhysicianLastVisit: Date;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
  })
  hadNotCompletedTest: string;

  @property({ getType: () => Diagnosis })
  hadNotCompletedTestDiagnosis: Diagnosis;

  @property({ getType: () => DiagnosisShort })
  disabledDetails: DiagnosisShort;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  toldHighBPCholest: boolean;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hadDrugsNotPreferred: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Been hospitalized for an emotional or mental disorder, or for any reason other than childbirth',
  })
  hadBeenHospitalized: boolean;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hadAlcoholRehab: boolean;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hadHIVTreatmentAdvice: boolean;

  @property({ getType: () => Customer })
  customer: Customer;

  @property({ rule: ['required'] })
  hadHIVTreatmentAdviceDiagnosis: Diagnosis;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: {
      true: 'Yes',
      false: 'No',
    },
  })
  hadHIVDiagnosed: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: {
      true: 'Yes',
      false: 'No',
    },
    comment:
      'Been diagnosed by a member of the medical profession with any disease or disorder of the heart, blood or blood vessels, lungs, liver, kidneys, thyroid, bones, joints, or the immune, neurological or gastrointestinal system',
  })
  hadCancerOrSyndrome: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: {
      true: 'Yes',
      false: 'No',
    },
    comment: 'has been diagnosed with cancer or diabetes',
  })
  hadCancerOrDiabetes: boolean;

  @property()
  haveNeitherHighBPCholest: boolean;

  showDiagnosisForOsteoarthritis() {
    return this.hasBMDMeds || this.hasBMDDisabilityBenefits;
  }

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'indicates if the primary physician visit was for routine medical care or not',
  })
  primaryPhysicianRoutine: string;

  @property({
    comment: 'details about the primary physician visit if it was not for routine care',
  })
  primaryPhysicianNotRoutineDetails: string;

  constructor() {
    super();
  }

  static new(customer) {
    return new PersonalHistory().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  hadHIVTreatmentAdviceTrigger() {
    if (this.hadHIVTreatmentAdvice) {
      if (!this.customer.application.primaryPolicy.insured.personalHistory.hadHIVTreatmentAdviceDiagnosis) {
        this.customer.application.primaryPolicy.insured.personalHistory.hadHIVTreatmentAdviceDiagnosis = Diagnosis.new(
          this.customer
        );
      }
    } else {
      this.customer.application.primaryPolicy.insured.personalHistory.hadHIVTreatmentAdviceDiagnosis = null;
    }
  }

  hadNotCompletedTestTrigger() {
    if (this.hadNotCompletedTest === 'Yes') {
      if (!this.hadNotCompletedTestDiagnosis) {
        this.hadNotCompletedTestDiagnosis = Diagnosis.new(this.customer);
      }
    } else {
      this.hadNotCompletedTestDiagnosis = null;
    }
  }

  hadHCHMedicationTrigger() {
    if (this.hadHCHMedication) {
      if (this.medsHCH.length === 0) {
        this.medsHCH.push(RX.new(this.customer));
      }
    } else {
      this.medsHCH = [];
    }
  }

  hasMedicationCholesterolTrigger() {
    if (this.hasMedicationCholesterol) {
      if (this.medsHCCholesterol.length === 0) {
        this.medsHCCholesterol.push(RX.new(this.customer));
      }
    } else {
      this.medsHCCholesterol = [];
    }
  }

  medicationsTrigger() {
    if (this.medications) {
      this.medsHCCholesterol = [];
    }
  }

  hadHCOtherTrigger() {
    if (this.hadHCOther) {
      if (this.hadHCOtherDiagnosis.length === 0) {
        this.hadHCOtherDiagnosis.push(Diagnosis.new(this.customer));
      }
    } else {
      this.hadHCOtherDiagnosis = [];
    }
  }

  hadCholestOtherTrigger() {
    if (this.hadCholestOther === true) {
      if (this.hadCholestOtherDiagnosis.length === 0) {
        this.hadCholestOtherDiagnosis.push(Diagnosis.new(this.customer));
      }
    } else {
      this.hadCholestOtherDiagnosis = [];
    }
  }

  hadBDOtherTrigger() {
    if (this.hadBDOther) {
      if (this.otherDiagnosisis.length === 0) {
        this.otherDiagnosisis.push(Diagnosis.new(this.customer));
      }
    } else {
      this.otherDiagnosisis = [];
    }
  }

  hadTBNotSureTrigger() {
    if (this.hadTBNotSure) {
      if (this.diagnosisTB.length === 0) {
        this.diagnosisTB.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagnosisTB = [];
    }
  }

  hadEONotSureTrigger() {
    if (this.hadEONotSure) {
      if (this.diagnosisEONotSure.length === 0) {
        this.diagnosisEONotSure.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagnosisEONotSure = [];
    }
  }

  hadEENTNotSureTrigger() {
    if (this.hadEENTNotSure) {
      if (this.diagnosisEENTNotSure.length === 0) {
        this.diagnosisEENTNotSure.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagnosisEENTNotSure = [];
    }
  }

  hadRDOtherTrigger() {
    if (this.hadRDOther) {
      if (this.diagnosisRDOther.length === 0) {
        this.diagnosisRDOther.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagnosisRDOther = [];
    }
  }

  hadBDNotSureTrigger() {
    if (this.hadBDNotSure) {
      if (this.diagnosisDDOther.length === 0) {
        this.diagnosisDDOther.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagnosisDDOther = [];
    }
  }

  hasBMDOtherTrigger() {
    if (this.hasBMDOther) {
      if (this.diagosisBMDAOther.length === 0) {
        this.diagosisBMDAOther.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagosisBMDAOther = [];
    }
  }

  hasRDOtherTrigger() {
    if (this.hasRDOther) {
      if (this.diagnosisRMDOther.length === 0) {
        this.diagnosisRMDOther.push(Diagnosis.new(this.customer));
      }
    } else {
      this.diagnosisRMDOther = [];
    }
  }

  hasDTNotSureTrigger() {
    if (this.hasDTNotSure) {
      if (this.DiagnosisisDTNotSure.length === 0) {
        this.DiagnosisisDTNotSure.push(Diagnosis.new(this.customer));
      }
    } else {
      this.DiagnosisisDTNotSure = [];
    }
  }

  hadUTDNotSureTrigger() {
    if (this.hadUTDNotSure) {
      if (this.DiagnosisUTDNotSure.length === 0) {
        this.DiagnosisUTDNotSure.push(Diagnosis.new(this.customer));
      }
    } else {
      this.DiagnosisUTDNotSure = [];
    }
  }

  hasSDOtherTrigger() {
    if (this.hasSDOther) {
      if (this.DiagnosisSDNotSure.length === 0) {
        this.DiagnosisSDNotSure.push(Diagnosis.new(this.customer));
      }
    } else {
      this.DiagnosisSDNotSure = [];
    }
  }

  hadIDNotSureTrigger() {
    if (this.hadIDNotSure) {
      if (this.DiagnosisIDNotSure.length === 0) {
        this.DiagnosisIDNotSure.push(Diagnosis.new(this.customer));
      }
    } else {
      this.DiagnosisIDNotSure = [];
    }
  }

  hadCPNotSureTrigger() {
    if (this.hadCPNotSure) {
      if (this.CPDetails.length === 0) {
        this.CPDetails.push(Diagnosis.new(this.customer));
      }
    } else {
      this.CPDetails = [];
    }
  }

  hadBloodTransfusionTrigger() {
    if (this.hadBloodTransfusion) {
      if (this.bloodTransfusionDetails.length === 0) {
        this.bloodTransfusionDetails.push(Surgery.new(this.customer));
      }
    } else {
      this.bloodTransfusionDetails = [];
    }
  }

  hadHeartSurgeryTrigger() {
    if (this.hadHeartSurgery) {
      if (this.heartSurgeryDetails.length === 0) {
        this.heartSurgeryDetails.push(Surgery.new(this.customer));
      }
    } else {
      this.heartSurgeryDetails = [];
    }
  }

  hasProstatectomyTrigger() {
    if (this.hasProstatectomy) {
      if (this.otherSurgeryDetails.length === 0) {
        this.otherSurgeryDetails.push(Surgery.new(this.customer));
      }
    } else {
      this.otherSurgeryDetails = [];
    }
  }

  hadMastectomyTrigger() {
    if (this.hadMastectomy) {
      if (this.otherSurgeryDetails.length === 0) {
        this.otherSurgeryDetails.push(Surgery.new(this.customer));
      }
    } else {
      this.otherSurgeryDetails = [];
    }
  }

  hadTreatmentOtherTrigger() {
    if (this.hadTreatmentOther) {
      if (this.hadTreatmentOtherDiagnosisCollection.length === 0) {
        this.hadTreatmentOtherDiagnosisCollection.push(Diagnosis.new(this.customer));
      }
    } else {
      this.hadTreatmentOtherDiagnosisCollection = [];
      this.hadTreatmentOtherDiagnosis = null;
    }
  }

  hadRXOtherTrigger() {
    if (this.hadRXOther) {
      if (this.hadMedsOther.length === 0) {
        this.hadMedsOther.push(RX.new(this.customer));
      }
    } else {
      this.hadMedsOther = [];
    }
  }

  primaryPhysicianTrigger() {
    if (!this.primaryPhysician) {
      this.primaryPhysicianName =
        this.primaryPhysicianPhone =
        this.primaryPhysicianLastVisitWhen =
        this.primaryPhysicianLastVisit =
          null;

      // clear the HIV address if it is primary physician
      if (this.hivPositiveWhichAddress === 'pp_address') {
        this.hivPositiveWhichAddress = undefined;
      }
    }
  }

  hadEOADepressionTreatmentStrTrigger() {
    if (this.hadEOADepressionTreatmentStr === 'Medication' || this.hadEOADepressionTreatmentStr === 'Both') {
      if (this.medsEOADepression.length === 0) {
        this.medsEOADepression.push(RX.new(this.customer));
      }
    } else {
      this.medsEOADepression = [];
    }
  }

  hasRDAMedsTrigger() {
    if (
      this.customer.application.primaryPolicy.isCoverpathPolicy() ||
      this.customer.application.primaryPolicy.isMMDirectPolicy()
    ) {
      if (this.hasRDAMeds) {
        if (this.medsRDAsthma.length === 0) {
          this.medsRDAsthma.push(RX.new(this.customer));
        }
      } else {
        this.medsRDAsthma = [];
      }
    }
  }

  getPrimaryPhysicianPhone() {
    const phone = this.primaryPhysicianPhone || '';
    const numb = phone.split(' ');

    if (Number(numb[1]) > 0) {
      return numb[0] + 'x ' + numb[1];
    }

    return '(' + numb[0].substr(0, 3) + ') ' + numb[0].substr(3, 3) + '-' + numb[0].substr(6, 4);
  }

  getPrimaryPhysicianAddress() {
    if (!this.primaryPhysician) {
      return '';
    }

    const personalHistory = this.customer.application.primaryPolicy.insured.personalHistory;
    const city = personalHistory.primaryPhysicianCity;
    const state = personalHistory.primaryPhysicianState;
    return `${city},${state}`;
  }

  getPrimaryPhysicianLastVisit() {
    // If a date was selected format it else return undefined or unknown
    const formatted =
      this.primaryPhysicianLastVisitWhen === 'lastvisit'
        ? Utils.dateToMY(this.primaryPhysicianLastVisit)
        : this.primaryPhysicianLastVisitWhen;
    return formatted ? formatted : '';
  }

  hivPositiveWhichAddressTrigger() {
    // clear address if address already known
    if (
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress &&
      /insured_address|pp_address|state_health|optout/.test(
        this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress
      )
    ) {
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddress = null;
    }
  }
}
